import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Mail, Lock, User, Eye, EyeOff, ArrowLeft, Car, Home, Award, PartyPopper, AlertCircle, RefreshCw, Phone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Separator } from '@/components/ui/separator';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { API } from '@/App';

const AuthPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const defaultTab = searchParams.get('tab') || 'login';
  const errorParam = searchParams.get('error');
  const fromNav = searchParams.get('from'); // Track if coming from navigation
  const intentParam = searchParams.get('intent'); // 'find' or 'list' for post-signup redirect
  
  const [activeTab, setActiveTab] = useState(defaultTab);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showEarlyHostDialog, setShowEarlyHostDialog] = useState(false);
  const [googleError, setGoogleError] = useState(null);
  const [sessionReady, setSessionReady] = useState(false);
  const [initAttempts, setInitAttempts] = useState(0);
  const [signupIntent, setSignupIntent] = useState(intentParam || null); // 'find' or 'list'
  
  // Login form
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  
  // Register form - set role based on intent
  const [registerData, setRegisterData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    name: '',
    phone: '',  // New phone field
    role: intentParam === 'list' ? 'host' : 'driver'
  });


  // Handle Google OAuth error from URL params
  useEffect(() => {
    if (errorParam) {
      let errorMessage = 'Google sign-in failed. Please try again.';
      
      if (errorParam.includes('denied') || errorParam.includes('access_denied')) {
        errorMessage = 'Google sign-in was cancelled.';
      } else if (errorParam.includes('token_exchange')) {
        errorMessage = 'Authentication failed. Please try again or use email login.';
      } else if (errorParam.includes('network')) {
        errorMessage = 'Network error. Please check your connection and try again.';
      } else if (errorParam.includes('invalid_state')) {
        errorMessage = 'Session expired. Please try again.';
      }
      
      setGoogleError(errorMessage);
      toast.error(errorMessage);
      
      // Clear the error from URL
      window.history.replaceState({}, '', '/auth');
    }
  }, [errorParam]);

  // Initialize session with retry logic
  const initializeSession = useCallback(async (retryCount = 0) => {
    try {
      const response = await fetch(`${API}/auth/session/init`, {
        method: 'GET',
        credentials: 'include',
        headers: { 'Accept': 'application/json' }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data?.status === 'ready') {
          setSessionReady(true);
          console.log('Auth session ready');
          return true;
        }
      }
    } catch (error) {
      console.warn(`Session init attempt ${retryCount + 1} failed:`, error.message);
      
      // Retry up to 3 times with increasing delay
      if (retryCount < 2) {
        await new Promise(resolve => setTimeout(resolve, (retryCount + 1) * 500));
        return initializeSession(retryCount + 1);
      }
    }
    
    // Even if init fails, allow the page to work (fallback to non-cookie auth)
    setSessionReady(true);
    return false;
  }, []);

  // Initialize session on mount
  useEffect(() => {
    initializeSession();
  }, [initializeSession]);

  // Handle auto-refresh if coming from landing page navigation (first visit)
  useEffect(() => {
    const hasRefreshed = sessionStorage.getItem('auth_page_refreshed');
    
    if (fromNav === 'landing' && !hasRefreshed) {
      sessionStorage.setItem('auth_page_refreshed', 'true');
      // Small delay then refresh to ensure clean state
      setTimeout(() => {
        window.location.replace('/auth');
      }, 100);
    }
    
    // Clear the flag after a short time so future visits can refresh again
    return () => {
      setTimeout(() => {
        sessionStorage.removeItem('auth_page_refreshed');
      }, 5000);
    };
  }, [fromNav]);

  const handleGoogleLogin = async () => {
    // Check if cookies are enabled
    if (!navigator.cookieEnabled) {
      toast.error('Please enable cookies to use Google login.');
      return;
    }
    
    setGoogleError(null);
    setLoading(true);
    
    // Build the API URL with origin for redirect URI selection
    const currentOrigin = window.location.origin;
    const apiUrl = `${API}/auth/google?redirect_uri=${encodeURIComponent('/dashboard')}&origin=${encodeURIComponent(currentOrigin)}`;
    
    try {
      // Try fetch first with a short timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 8000);
      
      const response = await fetch(apiUrl, {
        method: 'GET',
        credentials: 'include',
        signal: controller.signal,
        headers: {
          'Accept': 'application/json',
        }
      });
      
      clearTimeout(timeoutId);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data?.auth_url) {
        if (data.state) {
          sessionStorage.setItem('oauth_state', data.state);
        }
        window.location.href = data.auth_url;
      } else {
        throw new Error('No auth URL');
      }
    } catch (error) {
      console.error('Google login error:', error.name, error.message);
      
      // On any error, fall back to direct navigation
      // This bypasses fetch/CORS entirely - the backend will redirect to Google
      console.log('Falling back to direct navigation...');
      window.location.href = `${apiUrl}&direct=1`;
    }
  };

  const handleRetry = async () => {
    setGoogleError(null);
    setSessionReady(false);
    setInitAttempts(0);
    
    // Re-initialize session
    await initializeSession();
    
    toast.success('Session refreshed. Try again.');
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!loginData.email || !loginData.password) {
      toast.error('Please fill in all fields');
      return;
    }
    
    setLoading(true);
    setGoogleError(null);
    
    try {
      const res = await axios.post(`${API}/auth/login`, loginData, { withCredentials: true });
      
      // Store session token in localStorage as backup
      if (res.data.session_token) {
        localStorage.setItem('session_token', res.data.session_token);
      }
      
      toast.success('Welcome back!');
      // Navigate to dashboard with user data
      navigate('/dashboard', { state: { user: res.data }, replace: true });
    } catch (error) {
      console.error('Login error:', error);
      // Provide more specific error messages
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else if (error.response?.status === 401) {
        toast.error('Invalid email or password.');
      } else if (error.code === 'ERR_NETWORK') {
        toast.error('Network error. Please check your internet connection.');
      } else {
        toast.error('Login failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    
    if (!registerData.email || !registerData.password || !registerData.name) {
      toast.error('Please fill in all fields');
      return;
    }
    
    if (registerData.password !== registerData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (registerData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    // Validate phone number (Australian format)
    if (registerData.phone) {
      // Remove spaces and dashes for validation
      const cleanPhone = registerData.phone.replace(/[\s\-()]/g, '');
      // Check for valid Australian phone formats: 04XXXXXXXX, +614XXXXXXXX, 614XXXXXXXX
      const phoneRegex = /^(\+?61|0)?4\d{8}$/;
      if (!phoneRegex.test(cleanPhone)) {
        toast.error('Please enter a valid Australian mobile number (e.g., 0412 345 678)');
        return;
      }
    }
    
    setLoading(true);
    try {
      // Format phone to standard format before sending
      let formattedPhone = registerData.phone;
      if (formattedPhone) {
        const cleanPhone = formattedPhone.replace(/[\s\-()]/g, '');
        if (cleanPhone.startsWith('0')) {
          formattedPhone = '+61' + cleanPhone.substring(1);
        } else if (cleanPhone.startsWith('61')) {
          formattedPhone = '+' + cleanPhone;
        } else if (!cleanPhone.startsWith('+')) {
          formattedPhone = '+61' + cleanPhone;
        }
      }
      
      const res = await axios.post(`${API}/auth/register`, {
        email: registerData.email,
        password: registerData.password,
        name: registerData.name,
        phone: formattedPhone || null,
        role: registerData.role
      }, { withCredentials: true });
      
      // Store session token in localStorage as backup
      if (res.data.session_token) {
        localStorage.setItem('session_token', res.data.session_token);
      }
      
      // Determine redirect based on role selected (not signupIntent)
      let redirectPath = '/dashboard';
      if (registerData.role === 'driver') {
        redirectPath = '/search';
      } else if (registerData.role === 'host') {
        redirectPath = '/create-listing';
      }
      
      if (res.data.is_early_adopter) {
        setShowEarlyHostDialog(true);
      } else {
        toast.success('Account created successfully!');
        navigate(redirectPath, { state: { user: res.data }, replace: true });
      }
    } catch (error) {
      console.error('Registration error:', error);
      // Provide more specific error messages
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else if (error.response?.status === 400) {
        toast.error('Invalid registration data. Please check your information.');
      } else if (error.response?.status === 422) {
        toast.error('Please fill in all required fields correctly.');
      } else if (error.code === 'ERR_NETWORK') {
        toast.error('Network error. Please check your internet connection.');
      } else {
        toast.error('Registration failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Get redirect path based on role
  const getRedirectPath = () => {
    if (registerData.role === 'driver') return '/search';
    if (registerData.role === 'host') return '/create-listing';
    return '/dashboard';
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      {/* Early Host Congratulations Dialog */}
      <Dialog open={showEarlyHostDialog} onOpenChange={setShowEarlyHostDialog}>
        <DialogContent className="sm:max-w-md text-center">
          <DialogHeader>
            <div className="mx-auto w-16 h-16 bg-gradient-to-br from-amber-400 to-orange-500 rounded-full flex items-center justify-center mb-4">
              <PartyPopper className="w-8 h-8 text-white" />
            </div>
            <DialogTitle className="text-2xl font-bold">
              Welcome, Early Host! 🎉
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="bg-gradient-to-r from-amber-50 to-orange-50 p-4 rounded-xl border border-amber-200">
              <div className="flex items-center justify-center gap-2 mb-2">
                <Award className="w-5 h-5 text-amber-600" />
                <span className="font-semibold text-amber-700">You're one of our first 1,000 hosts!</span>
              </div>
              <p className="text-sm text-amber-600">
                As an Early Host, you'll have a special badge on all your listings, 
                helping you stand out from the crowd and attract more drivers.
              </p>
            </div>
            
            <p className="text-slate-600 text-sm">
              Thank you for being part of SpotFinder's founding community!
            </p>
          </div>
          
          <Button 
            onClick={() => {
              setShowEarlyHostDialog(false);
              navigate(getRedirectPath(), { replace: true });
            }} 
            className="w-full bg-[#5ebc67] hover:bg-[#4da855] rounded-full"
          >
            Get Started
          </Button>
        </DialogContent>
      </Dialog>

      <div className="w-full max-w-md">
        {/* Back button */}
        <Button 
          variant="ghost" 
          onClick={() => navigate('/')}
          className="mb-6 text-slate-600 hover:text-slate-900"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to home
        </Button>

        {/* Google OAuth Error Display with Retry */}
        {googleError && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-red-700 font-medium">Sign-in Error</p>
                <p className="text-red-600 text-sm">{googleError}</p>
              </div>
            </div>
            {initAttempts > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleRetry}
                className="mt-3 w-full text-red-600 border-red-200 hover:bg-red-50"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh & Try Again
              </Button>
            )}
          </div>
        )}

        <Card className="border-0 shadow-xl">
          <CardHeader className="text-center pb-2">
            <div className="flex justify-center mb-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_spotfinder-96/artifacts/rmerhn11_spotfinder-high-resolution-logo%20%281%29%20%281%29.png" 
                alt="SpotFinder" 
                className="h-10"
              />
            </div>
            <CardTitle className="text-2xl" style={{ fontFamily: 'Manrope' }}>
              {activeTab === 'login' ? 'Welcome back' : 'Create an account'}
            </CardTitle>
            <CardDescription>
              {activeTab === 'login' 
                ? 'Sign in to manage your bookings' 
                : 'Join SpotFinder to find or list parking'}
            </CardDescription>
          </CardHeader>
          
          <CardContent>
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid w-full grid-cols-2 mb-6">
                <TabsTrigger value="login" data-testid="login-tab">Log In</TabsTrigger>
                <TabsTrigger value="register" data-testid="register-tab">Sign Up</TabsTrigger>
              </TabsList>

              {/* Login Tab */}
              <TabsContent value="login">
                <form onSubmit={handleLogin} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="login-email">Email</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        id="login-email"
                        type="email"
                        placeholder="you@example.com"
                        value={loginData.email}
                        onChange={(e) => setLoginData(d => ({ ...d, email: e.target.value }))}
                        className="pl-10"
                        data-testid="login-email"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="login-password">Password</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        id="login-password"
                        type={showPassword ? 'text' : 'password'}
                        placeholder="••••••••"
                        value={loginData.password}
                        onChange={(e) => setLoginData(d => ({ ...d, password: e.target.value }))}
                        className="pl-10 pr-10"
                        data-testid="login-password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  <div className="flex justify-end">
                    <button
                      type="button"
                      onClick={() => navigate('/forgot-password')}
                      className="text-sm text-[#5ebc67] hover:underline"
                    >
                      Forgot password?
                    </button>
                  </div>

                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-[#5ebc67] hover:bg-[#4da855] rounded-full py-6"
                    data-testid="login-submit"
                  >
                    {loading ? 'Signing in...' : 'Sign In'}
                  </Button>
                </form>

                <div className="relative my-6">
                  <Separator />
                  <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-white px-4 text-sm text-slate-500">
                    or
                  </span>
                </div>

                <Button
                  type="button"
                  variant="outline"
                  onClick={handleGoogleLogin}
                  disabled={loading}
                  className="w-full rounded-full py-6"
                  data-testid="google-login"
                >
                  {loading ? (
                    <>
                      <div className="w-5 h-5 mr-2 border-2 border-slate-300 border-t-slate-600 rounded-full animate-spin"></div>
                      Connecting...
                    </>
                  ) : (
                    <>
                      <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                      </svg>
                      Continue with Google
                    </>
                  )}
                </Button>
              </TabsContent>

              {/* Register Tab */}
              <TabsContent value="register">
                <form onSubmit={handleRegister} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="register-name">Full Name</Label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        id="register-name"
                        type="text"
                        placeholder="John Smith"
                        value={registerData.name}
                        onChange={(e) => setRegisterData(d => ({ ...d, name: e.target.value }))}
                        className="pl-10"
                        data-testid="register-name"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="register-email">Email</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        id="register-email"
                        type="email"
                        placeholder="you@example.com"
                        value={registerData.email}
                        onChange={(e) => setRegisterData(d => ({ ...d, email: e.target.value }))}
                        className="pl-10"
                        data-testid="register-email"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="register-phone">Phone Number <span className="text-slate-400 text-xs font-normal">(Australian mobile)</span></Label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        id="register-phone"
                        type="tel"
                        placeholder="0412 345 678"
                        value={registerData.phone}
                        onChange={(e) => {
                          // Allow only numbers, spaces, and + symbol
                          const value = e.target.value.replace(/[^\d\s+\-()]/g, '');
                          setRegisterData(d => ({ ...d, phone: value }));
                        }}
                        maxLength={15}
                        className="pl-10"
                        data-testid="register-phone"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="register-password">Password</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        id="register-password"
                        type={showPassword ? 'text' : 'password'}
                        placeholder="At least 6 characters"
                        value={registerData.password}
                        onChange={(e) => setRegisterData(d => ({ ...d, password: e.target.value }))}
                        className="pl-10 pr-10"
                        data-testid="register-password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="register-confirm">Confirm Password</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        id="register-confirm"
                        type={showPassword ? 'text' : 'password'}
                        placeholder="••••••••"
                        value={registerData.confirmPassword}
                        onChange={(e) => setRegisterData(d => ({ ...d, confirmPassword: e.target.value }))}
                        className="pl-10"
                        data-testid="register-confirm"
                      />
                    </div>
                  </div>

                  <div className="space-y-3">
                    <Label>I want to:</Label>
                    <RadioGroup
                      value={registerData.role}
                      onValueChange={(value) => setRegisterData(d => ({ ...d, role: value }))}
                      className="grid grid-cols-2 gap-4"
                    >
                      <label
                        className={`flex items-center gap-3 p-4 rounded-xl border cursor-pointer transition-colors ${
                          registerData.role === 'driver'
                            ? 'border-[#5ebc67] bg-green-50'
                            : 'border-slate-200 hover:border-slate-300'
                        }`}
                      >
                        <RadioGroupItem value="driver" id="role-driver" />
                        <div className="flex items-center gap-2">
                          <Car className="w-5 h-5 text-slate-600" />
                          <span className="font-medium">Find Parking</span>
                        </div>
                      </label>
                      <label
                        className={`flex items-center gap-3 p-4 rounded-xl border cursor-pointer transition-colors ${
                          registerData.role === 'host'
                            ? 'border-[#5ebc67] bg-green-50'
                            : 'border-slate-200 hover:border-slate-300'
                        }`}
                      >
                        <RadioGroupItem value="host" id="role-host" />
                        <div className="flex items-center gap-2">
                          <Home className="w-5 h-5 text-slate-600" />
                          <span className="font-medium">List Driveway</span>
                        </div>
                      </label>
                    </RadioGroup>
                    <p className="text-xs text-slate-400 text-center mt-2">
                      You can do both later from your account settings
                    </p>
                  </div>

                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-[#5ebc67] hover:bg-[#4da855] rounded-full py-6"
                    data-testid="register-submit"
                  >
                    {loading ? 'Creating account...' : 'Create Account'}
                  </Button>
                </form>

                <div className="relative my-6">
                  <Separator />
                  <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-white px-4 text-sm text-slate-500">
                    or
                  </span>
                </div>

                <Button
                  type="button"
                  variant="outline"
                  onClick={handleGoogleLogin}
                  disabled={loading}
                  className="w-full rounded-full py-6"
                  data-testid="google-register"
                >
                  {loading ? (
                    <>
                      <div className="w-5 h-5 mr-2 border-2 border-slate-300 border-t-slate-600 rounded-full animate-spin"></div>
                      Connecting...
                    </>
                  ) : (
                    <>
                      <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                      </svg>
                      Sign up with Google
                    </>
                  )}
                </Button>

                <p className="text-xs text-slate-500 text-center mt-4">
                  By creating an account, you agree to our Terms of Service and Privacy Policy.
                </p>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AuthPage;
